import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { LayoutDashboard, FileText, Factory, Users, Settings, LogOut, Globe, Truck, Receipt, UserCog, Landmark, HardHat, Mail, ScrollText, BellRing, TrendingUp, ListTodo, Bell, Filter, MessageSquare, ShieldCheck } from "lucide-react";
import { useAuth } from "./AuthContext";
import { ChangePassword } from "./ChangePassword";
import api from "../lib/api";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/queue", label: "My Queue", icon: ListTodo },
  { to: "/admin/rfqs", label: "RFQs", icon: FileText },
  { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
  { to: "/admin/followups", label: "Follow-Ups", icon: BellRing, badge: true },
  { to: "/admin/orders", label: "Orders", icon: Truck },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt },
  { to: "/admin/revenue", label: "Revenue", icon: TrendingUp },
  { to: "/admin/funnel", label: "Funnel", icon: Filter },
  { to: "/admin/suppliers", label: "Suppliers", icon: Factory },
  { to: "/admin/crm", label: "CRM", icon: Users },
  { to: "/admin/government", label: "Gov Bids", icon: Landmark },
  { to: "/admin/federal", label: "Federal Pipeline", icon: ShieldCheck, agingBadge: true },
  { to: "/admin/contractors", label: "Contractors", icon: HardHat },
  { to: "/admin/team", label: "Team", icon: UserCog, superOnly: true },
  { to: "/admin/email-log", label: "Email Log", icon: Mail },
  { to: "/admin/audit", label: "Audit Trail", icon: ScrollText },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [followupCount, setFollowupCount] = useState(0);
  const [agingCount, setAgingCount] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    api.get("/admin/followups/count").then((r) => setFollowupCount(r.data.total || 0)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/admin/federal/pipeline/count").then((r) => setAgingCount(r.data.aging_14 || 0)).catch(() => {});
  }, [pathname]);
  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="admin-layout">
      <aside className="w-[230px] bg-[var(--pg-navy)] text-white flex-shrink-0 flex flex-col fixed inset-y-0 left-0 z-40">
        <div className="px-5 py-6 border-b border-white/10">
          <div className="font-[Poppins] font-bold text-[16px] tracking-tight">PATRICK GLOBAL</div>
          <div className="text-[9.5px] tracking-[0.18em] text-[var(--pg-accent)] uppercase font-semibold mt-0.5">Procurement Platform</div>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto py-4 px-3 space-y-1">
          {LINKS.filter((l) => !l.superOnly || user?.role === "super_admin").map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-[Poppins] font-medium transition-colors ${
                  isActive ? "bg-[var(--pg-accent)] text-white" : "text-white/70 hover:text-white hover:bg-white/10"
                }`
              }>
              <l.icon size={17} /> {l.label}
              {l.badge && followupCount > 0 && (
                <span data-testid="followup-nav-badge" className="ml-auto bg-amber-400 text-[var(--pg-navy)] text-[10.5px] font-[Poppins] font-bold px-1.5 py-0.5 rounded-full leading-none">{followupCount}</span>
              )}
              {l.agingBadge && agingCount > 0 && (
                <span data-testid="federal-aging-nav-badge" title={`${agingCount} requirement(s) 14+ days in one stage`} className="ml-auto bg-red-500 text-white text-[10.5px] font-[Poppins] font-bold px-1.5 py-0.5 rounded-full leading-none">{agingCount}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 pb-5 space-y-1 border-t border-white/10 pt-4">
          <Link to="/" data-testid="admin-view-site-link" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13.5px] text-white/70 hover:text-white hover:bg-white/10 transition-colors">
            <Globe size={16} /> View Website
          </Link>
          <div className="px-3 py-1 text-[12px] text-white/50 truncate">{user?.email}</div>
          <ChangePassword />
          <button data-testid="admin-logout-button" onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13.5px] text-white/70 hover:text-white hover:bg-white/10 transition-colors">
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-[230px] p-6 md:p-8 pr-20 md:pr-20 min-w-0">
        <AdminBell />
        <Outlet />
      </main>
    </div>
  );
}

const BELL_COLOR = { rfq: "bg-blue-500", supplier_quote: "bg-emerald-500", changes: "bg-amber-500" };

function AdminBell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = useCallback(() => {
    api.get("/admin/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load, pathname]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      api.post("/admin/notifications/mark-read")
        .then(() => setData((d) => ({ ...d, unread: 0 })))
        .catch(() => {});
    }
  };

  return (
    <div className="fixed top-5 right-6 z-50">
      <button data-testid="admin-notifications-bell" onClick={toggle} aria-label="Notifications"
        className="relative inline-flex items-center justify-center w-[42px] h-[42px] rounded-full bg-white border border-slate-200 shadow-sm text-slate-500 hover:text-[var(--pg-navy)] transition-colors">
        <Bell size={17} />
        {data.unread > 0 && (
          <span data-testid="admin-notifications-badge"
            className="absolute -top-1 -right-1 min-w-[19px] h-[19px] px-1 rounded-full bg-red-500 text-white text-[10.5px] font-[Poppins] font-bold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="admin-notifications-panel"
            className="absolute right-0 top-[50px] z-50 w-[360px] max-h-[440px] overflow-y-auto bg-white rounded-2xl border border-slate-200 shadow-xl">
            <div className="px-4 py-3 border-b border-slate-100 font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)]">Team Notifications</div>
            {data.items.length === 0 ? (
              <p data-testid="admin-notifications-empty" className="px-4 py-8 text-center text-[13px] text-slate-400">
                Nothing yet. New RFQs, supplier quotes and change requests will appear here.
              </p>
            ) : data.items.map((n) => (
              <button key={n.id} data-testid={`admin-notification-${n.id}`}
                onClick={() => { setOpen(false); if (n.link) navigate(n.link); }}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${n.read ? "" : "bg-blue-50/60"}`}>
                <div className="flex items-start gap-2.5">
                  <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${BELL_COLOR[n.type] || "bg-slate-400"}`} />
                  <div className="min-w-0">
                    <div className="text-[13px] font-[Poppins] font-semibold text-[var(--pg-navy)] leading-snug">{n.title}</div>
                    {n.message && <div className="mt-0.5 text-[12px] text-slate-500 leading-snug">{n.message}</div>}
                    <div className="mt-1 text-[11px] text-slate-400">{(n.created_at || "").slice(0, 10)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
